import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styles from 'styles';

import AutoComplete from 'material-ui/AutoComplete';
import RaisedButton from 'material-ui/RaisedButton';
import MenuItem from 'material-ui/MenuItem';

import api from 'services';
import MapComponent from './Map';

/**
 * MergingAreas Component
 *
 * @param {Object} props
 * @param {Function} props.mergeAreas
 * @param {Function} props.addAreaToArea
 * @param {Function} props.addValue
 * @param {Boolean} props.disabledBtn
 * @param {Boolean} props.disabledAddAreaToArea
 * @param {Boolean} props.clearAreas
 * @param {Object} [props.sourceArea]
 * @param {Object} [props.destinationArea]
 */
const MergingAreas = ({
    mergeAreas,
    addAreaToArea,
    addValue,
    disabledBtn,
    disabledAddAreaToArea,
    clearAreas,
    sourceArea,
    destinationArea,
}) => {
    const [sourceObject, setSourceObject] = useState({ name: '' });
    const [sourceItems, setSourceItems] = useState([]);
    const [destinationObject, setDestinationObject] = useState({ name: '' });
    const [destinationItems, setDestinationItems] = useState([]);

    useEffect(() => {
        if (clearAreas) {
            setSourceObject({ name: '' });
            setSourceItems([]);
            setDestinationObject({ name: '' });
            setDestinationItems([]);
        }
    }, [clearAreas]);

    useEffect(() => {
        if (!clearAreas && sourceArea) {
            setSourceObject(sourceArea);
        }
    }, [sourceArea, clearAreas]);

    useEffect(() => {
        if (!clearAreas && destinationArea) {
            setDestinationObject(destinationArea);
        }
    }, [destinationArea, clearAreas]);

    const merge = () => {
        mergeAreas();
    };

    const add = () => {
        addAreaToArea();
    };

    const updateInputSource = (searchText) => {
        const sourceObj = { name: searchText };
        setSourceObject(sourceObj);
        addValue('sourceArea', sourceObj);

        if (searchText.length <= 1) {
            return;
        }

        api.findPortAdmin({ name: searchText, onlyAreasWithChild: true })
            .then((response) => {
                if (response.status === 200) {
                    const items = [];

                    response.data.data.forEach((item) => {
                        items.push({
                            name: `${item.name} ${item.country ? `(${item.country.name})` : ''}`,
                            item: (
                                <MenuItem
                                    style={{ width: '100%' }}
                                    primaryText={`${item.name} ${item.country ? `(${item.country.name})` : ''}`}
                                    item={item}
                                />
                            ),
                        });

                        if (item.alt && item.alt.length > 0) {
                            item.alt.forEach((alternativeName) => {
                                items.push({
                                    name: `${alternativeName} (alternative name)`,
                                    item: (
                                        <MenuItem
                                            style={{ width: '100%', padding: '0 0 0 20px' }}
                                            primaryText={`${alternativeName} (alternative name)`}
                                            item={item}
                                        />
                                    ),
                                });
                            });
                        }

                        if (item.sub && item.sub.length > 0) {
                            item.sub.forEach((subPort) => {
                                items.push({
                                    name: `${subPort.name} ${item.country ? `(${item.country.name})` : ''}`,
                                    item: (
                                        <MenuItem
                                            style={{ width: '100%', padding: '0 0 0 20px' }}
                                            primaryText={`${subPort.name} ${item.country ? `(${item.country.name})` : ''}`}
                                            item={subPort}
                                        />
                                    ),
                                });
                            });
                        }
                    });

                    setSourceItems(items);
                    document.body.scrollTop += 1;
                    document.body.scrollTop -= 1;
                }
            });
    };

    const updateInputDestination = (searchText) => {
        const destObj = { name: searchText };
        setDestinationObject(destObj);
        addValue('destinationArea', {});

        if (searchText.length <= 1) {
            return;
        }

        api.findPortAdmin({ name: searchText, onlyAreasWithChild: true })
            .then((response) => {
                if (response.status === 200) {
                    const items = [];

                    response.data.data.forEach((item) => {
                        items.push({
                            name: `${item.name} ${item.country ? `(${item.country.name})` : ''}`,
                            item: (
                                <MenuItem
                                    style={{ width: '100%' }}
                                    primaryText={`${item.name} ${item.country ? `(${item.country.name})` : ''}`}
                                    item={item}
                                />
                            ),
                        });

                        if (item.alt && item.alt.length > 0) {
                            item.alt.forEach((alternativeName) => {
                                items.push({
                                    name: `${alternativeName} (alternative name)`,
                                    item: (
                                        <MenuItem
                                            style={{ width: '100%', padding: '0 0 0 20px' }}
                                            primaryText={`${alternativeName} (alternative name)`}
                                            item={item}
                                        />
                                    ),
                                });
                            });
                        }

                        if (item.sub && item.sub.length > 0) {
                            item.sub.forEach((subPort) => {
                                items.push({
                                    name: `${subPort.name} ${item.country ? `(${item.country.name})` : ''}`,
                                    item: (
                                        <MenuItem
                                            style={{ width: '100%', padding: '0 0 0 20px' }}
                                            primaryText={`${subPort.name} ${item.country ? `(${item.country.name})` : ''}`}
                                            item={subPort}
                                        />
                                    ),
                                });
                            });
                        }
                    });

                    setDestinationItems(items);
                    document.body.scrollTop += 1;
                    document.body.scrollTop -= 1;
                }
            });
    };

    const onUpdateInput = (searchText, type) => {
        switch (type) {
            case 'source':
                updateInputSource(searchText);
                break;
            case 'destination':
                updateInputDestination(searchText);
                break;
        }
    };

    const onChooseSource = (updateProperties) => {
        if (typeof updateProperties === 'string' && sourceItems.length > 0) {
            const sourceObj = sourceItems[0].item.props.item;
            setSourceObject(sourceObj);
            setSourceItems([]);
            addValue('sourceArea', sourceObj);
            return;
        }

        if (!updateProperties || typeof updateProperties === 'string') {
            return;
        }

        const sourceObj = updateProperties.item.props.item;
        setSourceObject(sourceObj);
        setSourceItems([]);
        addValue('sourceArea', sourceObj);
    };

    const onChooseDestination = (updateProperties) => {
        if (typeof updateProperties === 'string' && destinationItems.length > 0) {
            const destObj = destinationItems[0].item.props.item;
            setDestinationObject(destObj);
            setDestinationItems([]);
            addValue('destinationArea', destObj);
            return;
        }

        if (!updateProperties || typeof updateProperties === 'string') {
            return;
        }

        const destObj = updateProperties.item.props.item;
        setDestinationObject(destObj);
        setDestinationItems([]);
        addValue('destinationArea', destObj);
    };

    const onChooseAutoComplete = (updateProperties, type) => {
        switch (type) {
            case 'source':
                onChooseSource(updateProperties);
                break;
            case 'destination':
                onChooseDestination(updateProperties);
                break;
        }
    };

    return (
        <div>
            <div className="row">
                <h3 className="col-xs-3">
                    Merge area to area
                </h3>
            </div>
            <div className="row">
                <div className="col-xs-4">
                    <p style={{ fontSize: '12px' }}>
                        Auto Complete only areas (Search fields are name, alt name). <br />
                        Area doesn&apos;t have country field.
                    </p>
                    <AutoComplete
                        textFieldStyle={styles.autoCompleteCSS}
                        floatingLabelText="Source"
                        searchText={sourceObject.name}
                        dataSource={sourceItems}
                        dataSourceConfig={{ text: 'name', value: 'item' }}
                        onUpdateInput={(searchText) => { onUpdateInput(searchText, 'source'); }}
                        onNewRequest={(chosenRequest) => { onChooseAutoComplete(chosenRequest, 'source'); }}
                        filter={AutoComplete.noFilter}
                        style={{ width: '100%' }}
                        listStyle={{ maxHeight: '300px', overflow: 'auto' }}
                        fullWidth
                        popoverProps={{
                            canAutoPosition: true,
                            anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                            targetOrigin: { horizontal: 'left', vertical: 'top' },
                        }}
                    />
                </div>
                <div className="col-xs-4">
                    <p style={{ fontSize: '12px' }}>
                        Auto Complete only areas (Search fields are name, alt name). <br />
                        Area doesn&apos;t have country field.
                    </p>
                    <AutoComplete
                        textFieldStyle={styles.autoCompleteCSS}
                        floatingLabelText="Destination"
                        searchText={destinationObject.name}
                        dataSource={destinationItems}
                        dataSourceConfig={{ text: 'name', value: 'item' }}
                        onUpdateInput={(searchText) => { onUpdateInput(searchText, 'destination'); }}
                        onNewRequest={(chosenRequest) => { onChooseAutoComplete(chosenRequest, 'destination'); }}
                        filter={AutoComplete.noFilter}
                        style={{ width: '100%' }}
                        listStyle={{ maxHeight: '300px', overflow: 'auto' }}
                        fullWidth
                        popoverProps={{
                            canAutoPosition: true,
                            anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                            targetOrigin: { horizontal: 'left', vertical: 'top' },
                        }}
                    />
                </div>
                <div className="col-xs-4">
                    <div>
                        <RaisedButton
                            primary
                            disabled={disabledBtn}
                            label="merge areas"
                            onClick={merge}
                        />
                    </div>

                    <div style={{ margin: '10px 0 0 0' }}>
                        <RaisedButton
                            primary
                            disabled={disabledAddAreaToArea}
                            label="add area to area"
                            onClick={add}
                        />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-xs-4">
                    {!sourceObject.childs || sourceObject.childs.length === 0 ? null : (
                        <MapComponent
                            coordinates={sourceObject.childs}
                            type="area"
                            loadingElement={<div style={{ height: '100%' }} />}
                            containerElement={<div style={{ height: '300px' }} />}
                            mapElement={<div style={{ height: '100%' }} />}
                            areaPolygon={sourceObject.areaCoordinates}
                        />
                    )}
                    {sourceObject.childs && sourceObject.childs.length === 0
                        ? 'Area doesn\'t have ports'
                        : ''}
                </div>
                <div className="col-xs-4">
                    {!destinationObject.childs || destinationObject.childs.length === 0 ? null : (
                        <MapComponent
                            coordinates={destinationObject.childs}
                            type="area"
                            loadingElement={<div style={{ height: '100%' }} />}
                            containerElement={<div style={{ height: '300px' }} />}
                            mapElement={<div style={{ height: '100%' }} />}
                            areaPolygon={sourceObject.areaCoordinates}
                        />
                    )}
                    {destinationObject.childs && destinationObject.childs.length === 0
                        ? 'Area doesn\'t have ports'
                        : ''}
                </div>
            </div>
        </div>
    );
};

export default connect(
    (state, {
        mergeAreas, joinAreas, addValue, disabledBtn, clearAreas,
    }) => ({
        mergeAreas,
        joinAreas,
        addValue,
        disabledBtn,
        clearAreas,
    }),
    dispatch => bindActionCreators(
        {

        },
        dispatch,
    ),
)(MergingAreas);



// WEBPACK FOOTER //
// ./app/components/Regions/Merging/Areas.jsx